/**
 * Pure helpers extracted from ConnectorsView (P40.2) so they can be
 * re-exported without breaking fast-refresh's
 * "components-file-only-exports-components" rule.
 */
import type { TFunction } from "i18next";
import type { ConnectorRow } from "shared/api/types";

export type RagState = "red" | "amber" | "green" | "slate";

// Operator-friendly cadence formatter. Sub-minute stays in seconds;
// >= 60 rolls into "every N min" then "every N hour".
export function formatCadence(seconds: number | null, t?: TFunction): string {
  // Round 16 I14 -- in the reader's language when the caller has a `t`;
  // the English below stays the fallback the tests read.
  if (seconds === null) return t ? t("connectorTimes.manual") : "manual only";
  if (seconds < 60)
    return t
      ? t("connectorTimes.everySeconds", { count: seconds })
      : `every ${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60)
    return t
      ? t("connectorTimes.everyMinutes", { count: minutes })
      : `every ${minutes} min`;
  const hours = Math.round(minutes / 60);
  return t
    ? t("connectorTimes.everyHours", { count: hours })
    : `every ${hours} hour${hours === 1 ? "" : "s"}`;
}

/**
 * D5 -- a connector's state, told truthfully.
 *
 * Before: no success ever recorded returned "slate" whether or not the
 * connector was switched ON, so one that had been enabled for a month
 * and delivered nothing sat there looking neutral. And a DISABLED
 * connector with an old error streak shouted red, though a connector
 * you turned off is not failing.
 *
 * The question is not "has this ever worked" but "is this expected to
 * be working, and is it?".
 */
export function ragStateFor(row: ConnectorRow, now: number = Date.now()): RagState {
  // Off by choice. Not failing, not healthy -- not in use.
  if (!row.enabled) return "slate";
  if (row.error_streak > 0) return "red";
  // Enabled and has never delivered anything. Not an error yet, but
  // nobody should have to notice this for themselves.
  if (!row.last_succeeded_at) return "amber";
  if (!row.cadence_seconds) return "green";
  const ageMs = now - new Date(row.last_succeeded_at).getTime();
  return ageMs > row.cadence_seconds * 1000 * 2 ? "amber" : "green";
}

/** Connectors the operator is expected to act on: enabled, and either
 *  failing or silent. "14 connectors, 0 failing" counted neither. */
export function needsAttention(
  rows: ConnectorRow[],
  now: number = Date.now(),
): ConnectorRow[] {
  return rows.filter((r) => {
    const state = ragStateFor(r, now);
    return state === "red" || state === "amber";
  });
}

/**
 * Why a row is the colour it is, in words. An operator looking at an
 * amber row should not have to work out which of the two reasons it
 * is.
 */
export function stateReason(
  row: ConnectorRow,
  now: number = Date.now(),
  t?: TFunction,
): string | null {
  const say = (key: string, english: string, params: Record<string, unknown> = {}) =>
    t ? t(`connectorTimes.reason.${key}`, params) : english;
  if (!row.enabled)
    return say("notEnabled", "Not enabled -- this system is not being read.");
  if (row.error_streak > 0) {
    return say(
      "failing",
      `Failing: ${row.error_streak} run(s) in a row have errored.`,
      { count: row.error_streak },
    );
  }
  if (!row.last_succeeded_at) {
    return row.last_run_at
      ? say("neverSucceeded", "Enabled, and no run has ever succeeded.")
      : say(
          "neverRun",
          "Enabled, but it has never run. If you expected it to run on a schedule, check that a cadence is set and the connector scheduler is switched on (MARSAD_CONNECTOR_SCHEDULER_ENABLED).",
        );
  }
  if (row.cadence_seconds) {
    const ageMs = now - new Date(row.last_succeeded_at).getTime();
    if (ageMs > row.cadence_seconds * 1000 * 2) {
      return say(
        "overdue",
        "Overdue: the last success is older than twice its cadence.",
      );
    }
  }
  return null;
}

export function formatRelative(
  iso: string | null,
  now: number = Date.now(),
  t?: TFunction,
): string {
  if (!iso) return t ? t("connectorTimes.never") : "never";
  const diffMs = now - new Date(iso).getTime();
  if (diffMs < 60_000) return t ? t("connectorTimes.justNow") : "just now";
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60)
    return t
      ? t("connectorTimes.minutesAgo", { count: minutes })
      : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return t ? t("connectorTimes.hoursAgo", { count: hours }) : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return t ? t("connectorTimes.daysAgo", { count: days }) : `${days}d ago`;
}
